import { Link } from "react-router-dom";
import { Lock, TrendingUp, TrendingDown } from "lucide-react";
import { formatUsd, formatNum, timeAgo } from "@/lib/format";

export default function CoinCard({ coin, pair }) {
  const up = (coin.change_24h || 0) >= 0;
  return (
    <Link
      to={`/coin/${coin.id}`}
      data-testid="coin-card-item"
      className="card-sheen card-glow animate-rise group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#131824]/90 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/50 hover:shadow-[0_12px_32px_-8px_rgba(0,82,255,0.25)]"
    >
      <div
        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-20 blur-2xl transition-opacity group-hover:opacity-40"
        style={{ background: pair?.color || "#0052ff" }}
      />
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <img
            src={coin.image || "/logo.png"}
            alt={coin.symbol}
            className="h-12 w-12 rounded-xl border border-white/10 object-cover transition-transform duration-300 ease-out group-hover:scale-105"
            onError={(e) => (e.currentTarget.src = "/logo.png")}
          />
          {pair && (
            <img
              src={pair.logo}
              alt={pair.symbol}
              className="absolute -bottom-1.5 -right-1.5 h-6 w-6 rounded-full border-2 border-[#131824] bg-[#131824] object-cover"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono2 text-sm font-bold uppercase tracking-wide text-slate-50">
              ${coin.symbol}
            </span>
            <span
              className={`flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
                up ? "bg-[#00e699]/10 text-[#00e699]" : "bg-[#ff3b69]/10 text-[#ff3b69]"
              }`}
            >
              {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
              {Math.abs(coin.change_24h || 0).toFixed(1)}%
            </span>
          </div>
          <div className="truncate text-sm text-slate-400">{coin.name}</div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
          style={{
            borderColor: (pair?.color || "#0052ff") + "55",
            background: (pair?.color || "#0052ff") + "18",
            color: pair?.color || "#7fb0ff",
          }}
        >
          {pair?.logo && (
            <img src={pair.logo} alt="" className="h-3.5 w-3.5 rounded-full" onError={(e) => (e.currentTarget.style.display = "none")} />
          )}
          paired · {coin.pair_symbol}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border border-[#00e699]/30 bg-[#00e699]/10 px-2 py-1 text-[10px] font-semibold text-[#00e699]">
          <Lock size={10} /> locked
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/5 pt-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Market cap</div>
          <div className="font-mono2 text-sm font-bold text-slate-100">{formatUsd(coin.market_cap_usd)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Vol 24h</div>
          <div className="font-mono2 text-sm font-bold text-slate-100">{formatUsd(coin.volume_24h_usd)}</div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
        <span>{formatNum(coin.holders)} holders</span>
        <span>{timeAgo(coin.created_at)}</span>
      </div>
    </Link>
  );
}
